import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { OnboardingLayout } from './OnboardingLayout';
import { onboardingPath } from './onboardingRoutes';

interface OnboardingState {
  step: string;
  version: number;
  workspaceName: string;
  isComplete: boolean;
}

/**
 * PersonalSetup page (R6) — the personal flow's first step.
 *
 * Personal onboarding is its own calm, personal-first flow — not a
 * stripped-down business one. This step asks only for the name of the
 * user's space. There is deliberately no website field, no team/company
 * language, and no business framing. Submitting advances to the outcomes
 * step.
 */
export function PersonalSetupPage() {
  const [workspaceName, setWorkspaceName] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState('');
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const continueUrl = searchParams.get('continue') || '';

  useEffect(() => {
    async function loadState() {
      try {
        const response = await fetch('/api/onboarding/state', { credentials: 'include' });
        const data = await response.json();
        if (response.ok && data.state) {
          const state = data.state as OnboardingState;
          if (state.workspaceName) setWorkspaceName(state.workspaceName);
        }
      } catch {
        // Non-blocking — the user can still fill the form.
      } finally {
        setIsLoading(false);
      }
    }
    loadState();
  }, []);

  const handleContinue = async () => {
    if (!workspaceName.trim()) return;

    setIsSaving(true);
    setError('');

    try {
      const response = await fetch('/api/onboarding/step', {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          step: 'personal-setup',
          data: { workspaceName: workspaceName.trim() },
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        setError(data.error || 'Failed to save your space');
        return;
      }

      navigate(onboardingPath('personal', 'outcomes', continueUrl));
    } catch {
      setError('Network error. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return (
      <OnboardingLayout currentStep="personal-setup" workspaceType="personal" onboardingMode="personal">
        <div className="bg-white shadow-lg rounded-lg p-8 text-center">
          <div className="animate-spin w-8 h-8 border-2 border-blue-600 border-t-transparent rounded-full mx-auto"></div>
          <p className="text-gray-500 mt-4">Loading...</p>
        </div>
      </OnboardingLayout>
    );
  }

  return (
    <OnboardingLayout currentStep="personal-setup" workspaceType="personal" onboardingMode="personal">
      <div className="bg-white shadow-lg rounded-lg p-6 sm:p-8">
        <div className="text-center mb-8">
          <h1 className="text-2xl font-semibold text-gray-900">Set up your space</h1>
          <p className="text-gray-500 mt-2">
            A calm home for everything you want to stay on top of. Let's start with a name.
          </p>
        </div>

        <div className="max-w-md mx-auto space-y-6">
          <div>
            <label htmlFor="space-name" className="block text-sm font-medium text-gray-700 mb-2">
              What should we call your space?
            </label>
            <input
              id="space-name"
              type="text"
              value={workspaceName}
              onChange={(e) => setWorkspaceName(e.target.value)}
              placeholder="My Space"
              className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
            <p className="text-xs text-gray-400 mt-2">
              Just for you — you can rename it anytime.
            </p>
          </div>

          <div className="bg-gray-50 rounded-lg p-4">
            <h3 className="text-sm font-medium text-gray-700 mb-2">What's next</h3>
            <ul className="space-y-1.5 text-sm text-gray-500">
              <li>Choose what you'd like your space to help with</li>
              <li>See your plan</li>
              <li>That's it — your space is ready</li>
            </ul>
          </div>
        </div>

        {error && (
          <div className="max-w-md mx-auto mt-4 p-3 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-sm text-red-600">{error}</p>
          </div>
        )}

        <div className="max-w-md mx-auto mt-8 pt-6 border-t border-gray-100 flex justify-end">
          <button
            type="button"
            onClick={handleContinue}
            disabled={isSaving || !workspaceName.trim()}
            className="px-6 py-2.5 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {isSaving ? 'Saving...' : 'Continue'}
          </button>
        </div>
      </div>
    </OnboardingLayout>
  );
}
